import { useState, useEffect, useCallback } from "react";
import { useChatbot } from "@/hooks/useChatbot";
import { useIsMobile } from "@/hooks/use-mobile";
import ChatBubble from "./ChatBubble";
import ChatWindow from "./ChatWindow";
import ChatGreeting from "./ChatGreeting";

const GREETING_STORAGE_KEY = "chat_greeting_shown";
const CHAT_OPENED_KEY = "chat_has_opened";
// Dismissed state is now only in-memory (resets on page refresh)
const GREETING_DELAY_MS = 8000; // 8 seconds
const GREETING_AUTO_DISMISS_MS = 6000; // 6 seconds
const SCROLL_TRIGGER_PERCENT = 50;
const DEFAULT_BOTTOM = 24; // 24px = bottom-6

const ChatWidget = () => {
  const isMobile = useIsMobile();
  const {
    messages,
    isLoading,
    isOpen,
    setIsOpen,
    sendMessage,
    clearChat,
    isEnabled,
    isBlocked,
    trackQuickReplyClick,
  } = useChatbot();

  const [showGreeting, setShowGreeting] = useState(false);
  const [greetingShown, setGreetingShown] = useState(false);
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(DEFAULT_BOTTOM);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if greeting was already shown or chat was opened this session
  useEffect(() => {
    const wasShown = sessionStorage.getItem(GREETING_STORAGE_KEY);
    const wasOpened = sessionStorage.getItem(CHAT_OPENED_KEY);
    if (wasShown) {
      setGreetingShown(true);
    }
    if (wasOpened) {
      setHasOpenedChat(true);
    }
    // Note: isDismissed is intentionally not persisted - resets on page refresh
  }, []);

  // Listen for custom event to reopen chat from header
  // Listen for custom event to reopen chat from header
  useEffect(() => {
    const handleOpenChat = () => {
      setIsDismissed(false);
      setIsOpen(true);
    };

    window.addEventListener("chat-widget-open", handleOpenChat);
    return () => window.removeEventListener("chat-widget-open", handleOpenChat);
  }, [setIsOpen]);

  // Track footer attribution visibility to adjust chat bubble position
  useEffect(() => {
    const updateBottomOffset = () => {
      // Use requestAnimationFrame to batch layout reads and avoid forced reflow
      requestAnimationFrame(() => {
        const attribution = document.getElementById("footer-attribution");
        if (!attribution) {
          setBottomOffset(DEFAULT_BOTTOM);
          return;
        }

        const rect = attribution.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // If the attribution bar is visible in viewport
        if (rect.top < viewportHeight) {
          // Calculate how much of the attribution is visible
          const visibleAmount = viewportHeight - rect.top;
          // Add that amount plus a small margin to the bottom offset
          setBottomOffset(Math.max(DEFAULT_BOTTOM, visibleAmount + 16));
        } else {
          setBottomOffset(DEFAULT_BOTTOM);
        }
      });
    };

    window.addEventListener("scroll", updateBottomOffset, { passive: true });
    window.addEventListener("resize", updateBottomOffset, { passive: true });
    // Defer initial check to avoid forced reflow on mount
    requestAnimationFrame(updateBottomOffset);

    return () => {
      window.removeEventListener("scroll", updateBottomOffset);
      window.removeEventListener("resize", updateBottomOffset);
    };
  }, []);

  // Timer-based greeting trigger
  useEffect(() => {
    if (!isEnabled || greetingShown || isOpen) return;

    const timer = setTimeout(() => {
      if (!greetingShown && !isOpen) {
        setShowGreeting(true);
        setGreetingShown(true);
        sessionStorage.setItem(GREETING_STORAGE_KEY, "true");
      }
    }, GREETING_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isEnabled, greetingShown, isOpen]);

  // Scroll-based greeting trigger (fallback)
  useEffect(() => {
    if (!isEnabled || greetingShown || isOpen) return;

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent >= SCROLL_TRIGGER_PERCENT && !greetingShown && !isOpen) {
        setShowGreeting(true);
        setGreetingShown(true);
        sessionStorage.setItem(GREETING_STORAGE_KEY, "true");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isEnabled, greetingShown, isOpen]);

  // Auto-dismiss greeting after delay
  useEffect(() => {
    if (!showGreeting) return;

    const timer = setTimeout(() => {
      setShowGreeting(false);
    }, GREETING_AUTO_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [showGreeting]);

  const handleDismissGreeting = useCallback(() => {
    setShowGreeting(false);
  }, []);

  const handleOpenChat = useCallback(() => {
    setShowGreeting(false);
    setHasOpenedChat(true);
    sessionStorage.setItem(CHAT_OPENED_KEY, "true");
    setIsOpen(true);
  }, [setIsOpen]);

  const handleBubbleClick = useCallback(() => {
    setShowGreeting(false);
    if (!isOpen) {
      setHasOpenedChat(true);
      sessionStorage.setItem(CHAT_OPENED_KEY, "true");
    }
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const handleDismissBubble = useCallback(() => {
    setIsDismissed(true);
    // Not persisted to sessionStorage - will reset on page refresh
  }, []);

  if (!isEnabled) return null;
  if (isDismissed && !isOpen) return null;

  return (
    <>
      <ChatGreeting
        isVisible={showGreeting && !isOpen}
        onDismiss={handleDismissGreeting}
        onOpenChat={handleOpenChat}
      />
      <ChatWindow
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        messages={messages}
        isLoading={isLoading}
        onSendMessage={sendMessage}
        onClearChat={clearChat}
        isBlocked={isBlocked}
        onQuickReplyClick={trackQuickReplyClick}
      />
      <ChatBubble 
        isOpen={isOpen} 
        onClick={handleBubbleClick}
        onDismiss={handleDismissBubble}
        hasUnreadGreeting={showGreeting}
        hasOpenedChat={hasOpenedChat}
        bottomOffset={bottomOffset}
      />
    </>
  );
};

export default ChatWidget;
